import { estResultatTest, type SondeChampCle, type SondeCredentialPutResult } from './api.js'

/**
 * Modèle pur du panneau « Accès de Sonde » : ce qui part vers Sonde, et ce
 * que veulent dire ses codes. Séparé du composant pour être lu d'un coup d'œil.
 */

/**
 * Le corps `credential` de Sonde est STRICT : exactement les champs de
 * `credential.fields`, aucun en plus, et une chaîne vide est refusée (400).
 * Un champ facultatif laissé vide est donc OMIS ; un champ requis vide rend
 * la saisie incomplète (les boutons restent grisés).
 */
export function valeursCandidates(champs: SondeChampCle[], saisie: Record<string, string>): {
  complet: boolean
  credential: Record<string, string | number>
} {
  const credential: Record<string, string | number> = {}
  let complet = champs.length > 0
  for (const f of champs) {
    // Un secret se colle tel quel ; un identifiant perd ses espaces d'extrémité.
    const brut = saisie[f.name] ?? ''
    const v = f.secret ? brut : brut.trim()
    if (v === '') { if (f.required) complet = false; continue }
    if (f.type === 'integer') {
      const n = Number(v)
      if (!Number.isInteger(n)) { complet = false; continue }
      credential[f.name] = n
    } else {
      credential[f.name] = v
    }
  }
  return { complet, credential }
}

/** Les codes de test de Sonde en clair ; tout autre code : le message de Sonde. */
const CODES: Record<string, string> = {
  ROLE_NOT_READ_ONLY: 'Ce compte a plus de droits que la lecture : créez pour Sonde un compte en lecture seule, puis collez sa clé.',
  AUTH_REFUSED: 'Clé refusée par l’équipement : vérifiez-la (copiée en entier, compte actif).',
  NOT_IDRAC: 'L’adresse répond, mais ce n’est pas un iDRAC : vérifiez l’adresse de la source.',
  AUTH_BREAKER_STOPPED: 'Arrêt complet : saisir une nouvelle clé. Sonde a cessé d’essayer après plusieurs refus.',
  CERT_CHANGED: 'Le certificat de l’équipement a changé : vérifiez-le, puis faites-lui confiance.',
  TEST_THROTTLED: 'Un essai vient d’avoir lieu : attendre avant de réessayer (10 secondes au moins).',
  NETWORK: 'Sonde n’atteint pas l’équipement (réseau) : rien n’a pu être essayé.',
  TIMEOUT: 'L’équipement n’a pas répondu à temps.',
  COLLECTOR_UNAVAILABLE: 'Le collecteur de Sonde est injoignable : l’essai n’a pas eu lieu.',
  FORBIDDEN: 'Lecture non autorisée pour ce compte : la clé est acceptée mais ne voit pas tout (source dégradée).',
}

export function messageCodeTest(code: string | null, messageFr: string): string {
  if (code && CODES[code]) return CODES[code]!
  return messageFr
}

export function estCredentialPutResult(x: unknown): x is SondeCredentialPutResult {
  if (typeof x !== 'object' || x === null) return false
  const o = x as Record<string, unknown>
  const c = o['credential'] as Record<string, unknown> | null | undefined
  return typeof o['stored'] === 'boolean' && estResultatTest(o['test'])
    && typeof c === 'object' && c !== null && Array.isArray(c['fields'])
}
